import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { formatNumberPrecise } from '../Utils';

const MasterLog = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const ITEMS_PER_PAGE = 20;

    const buildUrl = useCallback((skip) => {
        let url = `/api/transaction/master-log?limit=${ITEMS_PER_PAGE}&skip=${skip}`;
        if (searchTerm) {
            const numericTerm = !isNaN(parseInt(searchTerm, 10)) ? parseInt(searchTerm, 10).toString() : searchTerm;
            url += `&participant=${encodeURIComponent(numericTerm)}`;
        }
        return url;
    }, [searchTerm]);

    useEffect(() => {
        let alive = true;
        setLoading(true); setError(null);
        apiGet(buildUrl(0))
            .then(newLogs => {
                if (!alive) return;
                setHasMore(newLogs.length === ITEMS_PER_PAGE);
                setLogs(newLogs);
            })
            .catch(() => { if (alive) setError('Failed to load transaction logs.'); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [buildUrl]);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const newLogs = await apiGet(buildUrl(logs.length));
            setHasMore(newLogs.length === ITEMS_PER_PAGE);
            setLogs(prev => [...prev, ...newLogs]);
        } catch { setError('Failed to load more.'); }
        finally { setLoadingMore(false); }
    }, [buildUrl, logs.length, loadingMore, hasMore]);

    const handleSearch = (e) => { e.preventDefault(); setSearchTerm(searchInput); };

    const PartyCell = ({ party }) => (
        <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{party.name || 'N/A'}</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace' }}>
                <i className="fas fa-id-badge" style={{ marginRight: 4 }} />ID: {party.id}
            </div>
        </div>
    );

    if (loading) return <div className="dark-loading"><div className="spinner" /><p>Loading Master History...</p></div>;
    if (error) return <div className="dark-error"><i className="fas fa-exclamation-circle" style={{ marginRight: 8 }} />{error}</div>;

    return (
        <div className="dark-page">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <h2 style={{ margin: 0 }}>Master Transaction Log</h2>
                <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8 }}>
                    <input
                        type="text"
                        className="dark-input"
                        style={{ width: 240 }}
                        placeholder="Search by User ID..."
                        value={searchInput}
                        onChange={e => setSearchInput(e.target.value)}
                    />
                    <button type="submit" style={{
                        background: 'linear-gradient(135deg,#ff4d6d,#ff2d55)', color: '#fff',
                        border: 'none', borderRadius: 8, padding: '9px 14px',
                        cursor: 'pointer', fontSize: 13
                    }}>
                        <i className="fas fa-search" />
                    </button>
                </form>
            </div>

            <div className="dark-table-wrap">
                <table className="dark-table">
                    <thead>
                        <tr>
                            <th><i className="fas fa-calendar-alt" style={{ marginRight: 6 }} />Date & Time</th>
                            <th>Type</th>
                            <th>Amount</th>
                            <th><i className="fas fa-user" style={{ marginRight: 6 }} />Sender</th>
                            <th><i className="fas fa-user" style={{ marginRight: 6 }} />Receiver</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.map(log => (
                            <tr key={log.id}>
                                <td style={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                                    {log.date ? new Date(log.date.seconds ? log.date.seconds * 1000 : log.date).toLocaleString() : ''}
                                </td>
                                <td>
                                    <span className={`badge ${log.type === 'USER_TRANSACTION' ? 'badge-green' : 'badge-yellow'}`}>
                                        {log.type.replace('_', ' ')}
                                    </span>
                                </td>
                                <td style={{ fontWeight: 700, color: '#ff4d6d', whiteSpace: 'nowrap' }}>
                                    {formatNumberPrecise(log.amount.toFixed(2))}
                                </td>
                                <td><PartyCell party={log.sender} /></td>
                                <td><PartyCell party={log.receiver} /></td>
                            </tr>
                        ))}
                        {logs.length === 0 && <tr className="empty-row"><td colSpan="5">No transaction logs found.</td></tr>}
                    </tbody>
                </table>
            </div>

            {hasMore && !searchTerm && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <button
                        onClick={handleLoadMore}
                        disabled={loadingMore}
                        style={{
                            background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)',
                            color: '#fff', padding: '10px 28px', borderRadius: 8,
                            fontSize: 13, fontWeight: 600, cursor: loadingMore ? 'not-allowed' : 'pointer',
                            opacity: loadingMore ? 0.6 : 1
                        }}
                    >
                        {loadingMore ? <><i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }} />Loading...</> : 'Load More'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default MasterLog;
